import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';
import RelatedBlogPosts from '../../../components/feature/RelatedBlogPosts';

export default function WhitbyServiceAreaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whitbyStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://alliancesecuritysystems.com/service-areas/whitby/#localbusiness',
      name: 'Alliance Security Systems - Whitby Office',
      description: 'Whitby\'s trusted security company since 1999. Professional alarm systems, security cameras, access control, and 24/7 live monitoring. 1,400+ local installations across Whitby, Brooklin, Ashburn, and Durham Region.',
      telephone: '+1-888-458-9181',
      foundingDate: '1999',
      url: 'https://alliancesecuritysystems.com/service-areas/whitby',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Durham Corporate Centre',
        addressLocality: 'Whitby',
        addressRegion: 'ON',
        postalCode: 'L1N 1C4',
        addressCountry: 'CA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '43.8975',
        longitude: '-78.9429',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '09:00',
          closes: '17:00',
          description: 'By Appointment',
        },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
      areaServed: [
        { '@type': 'City', name: 'Whitby' },
        { '@type': 'City', name: 'Brooklin' },
        { '@type': 'City', name: 'Ashburn' },
        { '@type': 'City', name: 'Ajax' },
        { '@type': 'City', name: 'Oshawa' },
        { '@type': 'City', name: 'Pickering' },
        { '@type': 'City', name: 'Courtice' },
        { '@type': 'City', name: 'Bowmanville' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Whitby Security Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems Whitby', description: 'Professional alarm system installation and 24/7 monitoring for Whitby homes and businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras Whitby', description: 'HD and 4K security camera systems with night vision and cloud storage for Whitby properties.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control Whitby', description: 'Commercial card access and keyless entry systems for Whitby businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Live Monitoring Whitby', description: 'Real-time video surveillance monitoring for Whitby properties.' } },
        ],
      },
      parentOrganization: {
        '@type': 'Organization',
        name: 'Alliance Security Systems',
        url: 'https://alliancesecuritysystems.com',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://alliancesecuritysystems.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Whitby', item: 'https://alliancesecuritysystems.com/service-areas/whitby' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Security Systems Installation Whitby',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems - Whitby Office',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Durham Corporate Centre',
          addressLocality: 'Whitby',
          addressRegion: 'ON',
          postalCode: 'L1N 1C4',
          addressCountry: 'CA',
        },
      },
      areaServed: {
        '@type': 'City',
        name: 'Whitby',
        containedInPlace: { '@type': 'AdministrativeArea', name: 'Ontario, Canada' },
      },
      serviceType: ['Alarm System Installation', 'Security Camera Installation', 'Access Control Installation', 'Live Video Monitoring'],
      description: 'Professional security system installation and monitoring services in Whitby, Ontario. Serving Downtown Whitby, Brooklin, Ashburn, Port Whitby, and surrounding Durham Region areas since 1999.',
    },
  ];

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Alarm Systems Whitby',
      description: 'Professional alarm system installation and monitoring for Whitby homes and businesses. 24/7 protection with instant mobile alerts and smart home integration.',
      link: '/alarm-packages'
    },
    {
      icon: 'ri-camera-line',
      title: 'Security Cameras Whitby',
      description: 'HD and 4K security camera systems with night vision, motion detection, and cloud storage for Whitby residential and commercial properties.',
      link: '/camera-packages'
    },
    {
      icon: 'ri-key-2-line',
      title: 'Access Control Whitby',
      description: 'Commercial card access and keyless entry systems for Whitby businesses. Secure entry management with detailed audit logs.',
      link: '/access-control'
    },
    {
      icon: 'ri-live-line',
      title: 'Live Monitoring Whitby',
      description: 'Real-time video surveillance monitoring for Whitby properties. Watch your property from anywhere, anytime with our mobile app.',
      link: '/live-monitoring'
    }
  ];

  const neighborhoods = [
    'Downtown Whitby', 'Brooklin', 'Ashburn', 'Port Whitby', 'Blue Grass Meadows',
    'Williamsburg', 'Pringle Creek', 'Lynde Creek', 'Rolling Acres', 'Taunton',
    'Whitby Shores', 'Cochrane', 'Country Lane', 'Dryden', 'Thickson'
  ];

  const stats = [
    { number: '1,400+', label: 'Whitby Installations' },
    { number: '25+', label: 'Years Serving Whitby' },
    { number: '4.9', label: 'Google Rating' },
    { number: '< 45min', label: 'Local Response Time' }
  ];

  return (
    <>
      <SEO
        title="Security Systems Whitby | Alarm & Camera Installation | Alliance Security"
        description="Whitby\'s trusted security company since 1999. Professional alarm systems, security cameras, access control at Durham Corporate Centre. 1,400+ local installations. Free consultation. Call 1-888-458-9181."
        keywords="security systems Whitby, alarm systems Whitby, security cameras Whitby, access control Whitby, Whitby security company, security installation Whitby ON, Durham Region security"
        canonical="/service-areas/whitby"
        structuredData={whitbyStructuredData}
      />
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>
                Home
              </Link>
              <Link to="/service-areas" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#D4A853]' : 'text-white'}`}>
                Service Areas
              </Link>
              <a href="tel:18884589181" className="bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer">
                1-888-458-9181
              </a>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-[#0A1628]/95 backdrop-blur-sm'} border-t ${isScrolled ? 'border-gray-100' : 'border-white/10'} px-4 pb-4 space-y-2`}>
              <Link to="/" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>
                Home
              </Link>
              <Link to="/service-areas" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-[#D4A853] border-gray-50' : 'text-[#D4A853] border-white/10'}`}>
                Service Areas
              </Link>
              <a href="tel:18884589181" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold mt-2">
                1-888-458-9181
              </a>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-[50vh] sm:min-h-[70vh] flex items-center pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/8ac9ba7351cbbb4caf7c6a37738d57e5.jpg" 
              alt="Whitby Ontario Waterfront"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <i className="ri-map-pin-2-fill text-[#D4A853] text-lg sm:text-xl"></i>
                <span className="text-[#D4A853] font-semibold text-sm sm:text-base">Whitby, Ontario</span>
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                <strong>Security Systems Whitby</strong> - Durham Region&apos;s Trusted Protector Since 1999
              </h1>
              <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8">
                Whitby&apos;s trusted security company with over 1,400 local installations. Professional alarm systems, security cameras, and access control for homes and businesses across Durham Region.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4">
                <Link 
                  to="/#contact"
                  className="w-full sm:w-auto bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Get Free Quote in Whitby
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </Link>
                <a 
                  href="tel:18884589181"
                  className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>

      {/* Breadcrumb */}
      <nav className="bg-white border-b border-gray-100 py-3" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <ol className="flex items-center gap-2 text-xs text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                <span itemProp="name">Home</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/service-areas" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                <span itemProp="name">Service Areas</span>
              </Link>
              <meta itemProp="position" content="2" />
            </li>
            <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-[#D4A853] font-medium" itemProp="name">Whitby</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </div>
      </nav>

        {/* Stats Section */}
        <section className="py-8 sm:py-12 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl sm:text-4xl font-bold text-[#D4A853] mb-1 sm:mb-2">{stat.number}</p>
                  <p className="text-white/70 text-xs sm:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                WHITBY SECURITY SERVICES
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Professional Security Solutions in Whitby
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive security services tailored for Whitby homes and businesses across Durham Region
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-xl sm:rounded-2xl p-5 sm:p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">{service.description}</p>
                  <Link to={service.link} className="text-[#D4A853] font-semibold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                  WHY WHITBY TRUSTS US
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Whitby&apos;s Most Trusted Security Company
                </h2>
                <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
                  For over 25 years, we&apos;ve been protecting Whitby homes and businesses with state-of-the-art security solutions. Our local Whitby office at Durham Corporate Centre ensures fast response times across the entire Durham Region.
                </p>
                
                <div className="space-y-3 sm:space-y-4">
                  {[
                    'Licensed and insured Whitby security professionals',
                    'Same-day service available across Durham Region',
                    'Factory-certified technicians for all major brands',
                    '24/7 local monitoring and support',
                    'Free on-site security assessments in Whitby',
                    'Local office at Durham Corporate Centre'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20installing%20modern%20alarm%20system%20panel%20in%20a%20beautiful%20Whitby%20Ontario%20home%20interior%20clean%20professional%20work%20environment%20residential%20security%20installation&width=800&height=600&seq=whitby2&orientation=landscape" 
                  alt="Security Installation Whitby"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

      {/* Related Blog Posts */}
      <RelatedBlogPosts city="whitby" />

        {/* Why a Local Expert Matters */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">LOCAL ADVANTAGE</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Why a Local Security Expert Matters in Whitby</h2>
              <p className="text-sm sm:text-lg text-white/70 max-w-2xl mx-auto">National chains can't match the speed, knowledge, and accountability of a true local provider</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: 'Under 45-Min Response',
                  description: 'Our Durham Region office is right here in Whitby — not a distant call centre. Whitby emergencies get fast, local responses across Brooklin, Ashburn, and all neighbourhoods.',
                },
                {
                  icon: 'ri-government-line',
                  title: 'Whitby Bylaw Knowledge',
                  description: 'We know Durham Region\'s alarm permit requirements, false alarm bylaws, and Town of Whitby regulations — so your system stays fully compliant.',
                },
                {
                  icon: 'ri-user-star-line',
                  title: 'Local References',
                  description: '"Alliance secured our Brooklin home — same-day installation, and they knew exactly what Durham Region\'s new development requirements needed." — Whitby homeowner',
                },
                {
                  icon: 'ri-percent-line',
                  title: 'Ontario Insurance Discounts',
                  description: 'Monitored security systems qualify for up to 20% home insurance discounts in Ontario. Our ULC-certified monitoring satisfies all major insurers including Intact and Aviva.',
                },
              ].map((point, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-[#D4A853]/20 rounded-full flex items-center justify-center mb-4">
                    <i className={`${point.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">{point.title}</h3>
                  <p className="text-white/65 text-xs sm:text-sm leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Office Location Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                  WHITBY OFFICE
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Serving Whitby & Durham Region
                </h2>
                <div className="space-y-4 mb-6 sm:mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-2-line text-[#D4A853] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-[#0A1628] text-sm sm:text-base">Address</p>
                      <p className="text-gray-600 text-sm">Durham Corporate Centre</p>
                      <p className="text-gray-600 text-sm">Whitby, ON L1N 1C4</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-[#D4A853] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-[#0A1628] text-sm sm:text-base">Phone</p>
                      <a href="tel:18884589181" className="text-gray-600 text-sm hover:text-[#D4A853] transition-colors cursor-pointer">1-888-458-9181</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-[#D4A853] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-[#0A1628] text-sm sm:text-base">Hours</p>
                      <p className="text-gray-600 text-sm">Mon–Fri: 8:00 AM – 6:00 PM</p>
                      <p className="text-gray-600 text-sm">Sat: 9:00 AM – 5:00 PM (By Appointment)</p>
                    </div>
                  </div>
                </div>
                <Link 
                  to="/#contact"
                  className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92234.37!2d-78.9429!3d43.8975!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d51d21ccd37533%3A0xdd8cbe15a1a1ed05!2sWhitby%2C%20ON!5e0!3m2!1sen!2sca!4v1699999999999!5m2!1sen!2sca"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Whitby Office Location Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* Neighborhoods Section */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                SERVICE COVERAGE
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Whitby Neighborhoods We Serve
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Providing security services across all Whitby neighborhoods and surrounding Durham Region communities
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center hover:bg-[#D4A853]/10 hover:border-[#D4A853]/30 border border-gray-100 transition-all duration-300"
                >
                  <span className="text-[#0A1628] font-medium text-xs sm:text-sm">{neighborhood}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
              Ready to Secure Your Whitby Property?
            </h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8">
              Get a free security assessment from Whitby&apos;s most trusted security experts at Durham Corporate Centre
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4">
              <Link 
                to="/#contact"
                className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                Schedule Free Consultation
              </Link>
              <a 
                href="tel:18884589181"
                className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                1-888-458-9181
              </a>
            </div>
          </div>
        </section>

        {/* Other Service Areas */}
        <section className="py-10 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1E293B] mb-6 sm:mb-8 text-center">Other GTA Service Areas</h3>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {[
                { name: 'Toronto', slug: 'toronto' },
                { name: 'Mississauga', slug: 'mississauga' },
                { name: 'Brampton', slug: 'brampton' },
                { name: 'Oakville', slug: 'oakville' },
                { name: 'Hamilton', slug: 'hamilton' },
                { name: 'Richmond Hill', slug: 'richmond-hill' },
                { name: 'Vaughan', slug: 'vaughan' },
                { name: 'Ajax', slug: 'ajax' },
                { name: 'Oshawa', slug: 'oshawa' },
              ].map((city) => (
                <Link
                  key={city.slug}
                  to={`/service-areas/${city.slug}`}
                  className="bg-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:text-[#D4A853] hover:shadow-md transition-all cursor-pointer border border-gray-200"
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                  <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                </div>
                <span className="text-white/60 text-xs sm:text-sm">Whitby Security Experts Since 1999</span>
              </div>
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                <Link to="/" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Home</Link>
                <Link to="/service-areas" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Service Areas</Link>
                <Link to="/privacy-policy" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
